import { Card, Avatar, Tabs, Tab, AppBar, CircularProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import GuestBadges from "components/GuestBadges";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import dayjs from "utils/dayjs";
import {
  FaBed,
  FaPlus,
  FaClock,
  FaCalendarCheck,
  FaHistory,
  FaStickyNote,
  FaCalendarAlt,
  FaChevronUp,
  FaChevronDown,
  FaArrowLeft,
} from "react-icons/fa";
import { RiSparkling2Line } from "react-icons/ri";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import DateRangeFilter from "components/DateRangePicker";
import VuiInput from "components/VuiInput";
import NotesColumn from "./utils/NotesColumn";
import {
  createGuestProfileNote,
  getGuestDetails,
  getGuestReservationNotes,
  getGuestStays,
  resetCreateGuestProfileNoteState,
  resetUpdateGuestProfileNoteState,
  updateGuestProfileNote,
} from "../../../../store/slice/guestSlice";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";

const { black, dark, primary, success, light } = colors;
const { borderWidth } = borders;

const sectionStyles = {
  background: rgba(black.main, 0.025),
  border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
  boxShadow: "none",
};

const subtleSectionStyles = {
  background: rgba(black.main, 0.018),
  border: `${borderWidth[1]} solid ${rgba(black.main, 0.07)}`,
};

const dashedSectionStyles = {
  background: rgba(black.main, 0.018),
  border: `${borderWidth[1]} dashed ${rgba(black.main, 0.14)}`,
};

const formatDate = (date) => (date ? dayjs.tz(date).format("MMM D, YYYY") : "—");

const formatKey = (key) => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStayDuration = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return "—";

  const start = dayjs.tz(checkIn).startOf("day");
  const end = dayjs.tz(checkOut).startOf("day");

  const nights = end.diff(start, "day");

  if (nights <= 0) return "—";

  return `${nights} night${nights > 1 ? "s" : ""}`;
};

const StayTile = ({ stay }) => {
  const [expanded, setExpanded] = useState(false);

  const preferences = stay?.preferences || {};
  const aiSummary = stay?.aiSummary;

  const hasPreferences = Object.keys(preferences).length > 0;
  const hasAiSummary = !!aiSummary;
  const hasExtraData = hasPreferences || hasAiSummary;

  return (
    <VuiBox p={2} mb={1.5} borderRadius="14px" sx={sectionStyles}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center">
        <VuiTypography variant="button" color="dark" fontWeight="bold">
          {stay.hotelName || "Not Provided"}
        </VuiTypography>

        <VuiBox
          sx={{
            cursor: "pointer",
            color: dark.main,
            opacity: 0.7,
            "&:hover": {
              opacity: 1,
            },
          }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </VuiBox>
      </VuiBox>

      <VuiBox
        mt={1.2}
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
        gap={2}
      >
        <VuiBox>
          <VuiTypography variant="caption" color="dark" fontWeight="regular" sx={{ opacity: 0.7 }}>
            Stay Period
          </VuiTypography>

          <VuiTypography variant="body2" color="dark" fontWeight="medium">
            <FaCalendarAlt size={10} style={{ marginRight: 6 }} />
            {formatDate(stay.checkIn)} → {formatDate(stay.checkOut)}
          </VuiTypography>
        </VuiBox>

        <VuiBox>
          <VuiTypography variant="caption" color="dark" fontWeight="regular" sx={{ opacity: 0.7 }}>
            Duration
          </VuiTypography>

          <VuiTypography variant="body2" color="dark" fontWeight="medium">
            <FaClock size={10} style={{ marginRight: 6 }} />
            {getStayDuration(stay.checkIn, stay.checkOut)}
          </VuiTypography>
        </VuiBox>

        <VuiBox>
          <VuiTypography variant="caption" color="dark" fontWeight="regular" sx={{ opacity: 0.7 }}>
            Room
          </VuiTypography>

          <VuiTypography variant="body2" color="dark" fontWeight="medium">
            <FaBed size={10} style={{ marginRight: 6 }} />
            {stay.room || "—"}
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      {expanded && (
        <VuiBox
          mt={2}
          pt={2}
          sx={{ borderTop: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}` }}
        >
          {!hasExtraData ? (
            <VuiBox
              py={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <VuiTypography
                variant="caption"
                color="dark"
                sx={{
                  opacity: 0.65,
                  fontSize: "13px",
                  letterSpacing: "0.02em",
                }}
              >
                No preferences and summary available for this reservation.
              </VuiTypography>
            </VuiBox>
          ) : (
            <>
              {hasAiSummary && (
                <VuiBox mb={2}>
                  <VuiTypography
                    variant="caption"
                    color="dark"
                    fontWeight="bold"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <RiSparkling2Line size={12} />
                    Stay Summary (AI)
                  </VuiTypography>

                  <VuiTypography
                    variant="body2"
                    color="dark"
                    mt={0.5}
                    lineHeight={1.6}
                    sx={{
                      fontSize: "13px",
                      letterSpacing: "0.03em",
                      opacity: 0.85,
                    }}
                  >
                    {aiSummary}
                  </VuiTypography>
                </VuiBox>
              )}

              {hasPreferences && (
                <VuiBox>
                  <VuiTypography variant="caption" color="dark" fontWeight="bold" mb={1}>
                    Preferences
                  </VuiTypography>

                  <VuiBox
                    display="grid"
                    gridTemplateColumns="repeat(auto-fit, minmax(220px,1fr))"
                    gap={1.5}
                  >
                    {Object.entries(preferences).map(([section, items]) => (
                      <VuiBox key={section} p={1.2} borderRadius="10px" sx={subtleSectionStyles}>
                        <VuiTypography variant="button" color="dark" fontWeight="bold" mb={0.5}>
                          {section}
                        </VuiTypography>

                        {items.map((item, i) => {
                          const key = Object.keys(item)[0];
                          const value = item[key];

                          return (
                            <VuiTypography
                              key={i}
                              variant="caption"
                              color="dark"
                              display="block"
                              sx={{ opacity: 0.78 }}
                            >
                              {formatKey(key)}:{" "}
                              {typeof value === "object"
                                ? Object.keys(value)
                                    .filter((k) => value[k])
                                    .map(formatKey)
                                    .join(", ")
                                : value?.toString()}
                            </VuiTypography>
                          );
                        })}
                      </VuiBox>
                    ))}
                  </VuiBox>
                </VuiBox>
              )}
            </>
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
};

const ProfileHistory = () => {
  const { guestId } = useParams();
  const { DISPATCH, STORE, NAVIGATE } = useCommon();
  const { can } = usePermissions();

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const {
    guestDetails,
    guestCounters,
    guestProfileNotes,
    guestStays,
    guestNotes,
    guestDetailsLoading,
    createGuestProfileNoteLoading,
    updateGuestProfileNoteLoading,
  } = STORE((state) => state.guests);

  const isLoading = guestDetailsLoading || !guestDetails || !guestCounters;
  const saveProfileNoteLoading = createGuestProfileNoteLoading || updateGuestProfileNoteLoading;
  const latestProfileNote = Array.isArray(guestProfileNotes) ? guestProfileNotes[0] : null;

  const guest = {
    id: guestDetails?.guestId,
    name: guestDetails?.name || "Guest",
    email: guestDetails?.email,
    phone: guestDetails?.phone,

    guestProfileNote: latestProfileNote?.text || "",
    guestProfileNoteId: latestProfileNote?.noteId || null,

    aiSummary: guestDetails?.latestStayAiSummary?.text,
    nationality: guestDetails?.nationality,
    language: guestDetails?.language,

    stays: guestCounters?.totalStays || 0,
    firstStay: guestCounters?.firstStayCheckInDate,
    lastStay: guestCounters?.mostRecentStayCheckOutDate,
    avgStayDuration: guestCounters?.avgStayDurationDays || 0,

    aiSummary: guestDetails?.latestStayAiSummary,

    stayHistory: (guestStays || []).map((stay) => ({
      reservationId: stay.reservationId,
      hotelName: stay.hotelName,
      room: stay.roomName,
      checkIn: stay?.stay?.checkInDate,
      checkOut: stay?.stay?.checkOutDate,
      aiSummary: stay?.aiSummary,

      preferences: (stay?.preferences?.completed || []).reduce((acc, section) => {
        acc[section.category] = (section.items || []).map((item) => ({
          [item.questionKey]: item.value,
        }));
        return acc;
      }, {}),
    })),

    notes: [
      ...(guestNotes?.arrival_note || []).map((note) => ({
        text: note.text,
        type: "arrival",
        staff: note.authorName,
        date: note.createdAt,
        stayDuration: `${formatDate(note?.stay?.checkInDate)} → ${formatDate(
          note?.stay?.checkOutDate
        )}`,
      })),

      ...(guestNotes?.staff_note || []).map((note) => ({
        text: note.text,
        type: "staff",
        staff: note.authorName,
        date: note.createdAt,
        stayDuration: `${formatDate(note?.stay?.checkInDate)} → ${formatDate(
          note?.stay?.checkOutDate
        )}`,
      })),

      ...(guestNotes?.pms_note || []).map((note) => ({
        text: note.text,
        type: "pms",
        staff: note.authorName,
        date: note.createdAt,
        stayDuration: `${formatDate(note?.stay?.checkInDate)} → ${formatDate(
          note?.stay?.checkOutDate
        )}`,
      })),
    ],
  };

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState(["", ""]);
  const [isEditingProfileNote, setIsEditingProfileNote] = useState(false);
  const [profileNoteValue, setProfileNoteValue] = useState("");

  const arrivalNotes = guest.notes?.filter((n) => n.type === "arrival") || [];
  const staffNotes = guest.notes?.filter((n) => n.type === "staff") || [];
  const pmsNotes = guest.notes?.filter((n) => n.type === "pms") || [];

  const initials =
    guest.name
      ?.split(" ")
      ?.map((p) => p[0])
      ?.join("")
      ?.toUpperCase() || "G";

  const filteredStays = guest.stayHistory.filter((stay) => {
    const matchesSearch =
      !debouncedSearch ||
      stay.room?.toLowerCase().includes(debouncedSearch) ||
      stay.hotelName?.toLowerCase().includes(debouncedSearch);

    const [startDate, endDate] = dateRange || ["", ""];

    if (!startDate || !endDate) {
      return matchesSearch;
    }

    const stayCheckIn = dayjs.tz(stay.checkIn).startOf("day");
    const stayCheckOut = dayjs.tz(stay.checkOut).startOf("day");

    const start = dayjs.tz(startDate).startOf("day");
    const end = dayjs.tz(endDate).endOf("day");

    const matchesDateRange =
      (stayCheckOut.isSame(start) || stayCheckOut.isAfter(start)) &&
      (stayCheckIn.isSame(end) || stayCheckIn.isBefore(end));

    return matchesSearch && matchesDateRange;
  });

  const filterNotes = (notes) =>
    notes.filter(
      (note) =>
        note.text.toLowerCase().includes(search.toLowerCase()) ||
        (note.staff && note.staff.toLowerCase().includes(search.toLowerCase()))
    );

  const handleAddProfileNote = () => {
    setProfileNoteValue(guest?.guestProfileNote || "");
    setIsEditingProfileNote(true);
  };

  const handleEditProfileNote = () => {
    setProfileNoteValue(guest?.guestProfileNote || "");
    setIsEditingProfileNote(true);
  };

  const handleCancelProfileNote = () => {
    setProfileNoteValue(guest?.guestProfileNote || "");
    setIsEditingProfileNote(false);
  };

  const handleSaveProfileNote = async () => {
    const trimmed = profileNoteValue.trim();
    if (!trimmed || !guest?.id || !hotelIds?.length) return;

    try {
      if (guest.guestProfileNoteId) {
        await DISPATCH(
          updateGuestProfileNote({
            guestId: guest.id,
            noteId: guest.guestProfileNoteId,
            text: trimmed,
          })
        ).unwrap();

        DISPATCH(resetUpdateGuestProfileNoteState());
      } else {
        await DISPATCH(
          createGuestProfileNote({
            guestId: guest.id,
            text: trimmed,
            createdFromHotelId: hotelIds[0],
          })
        ).unwrap();

        DISPATCH(resetCreateGuestProfileNoteState());
      }

      setIsEditingProfileNote(false);
    } catch (error) {
      console.error("Failed to save guest profile note:", error);
    }
  };

  const arrivalNotesFiltered = filterNotes(arrivalNotes);
  const staffNotesFiltered = filterNotes(staffNotes);
  const pmsNotesFiltered = filterNotes(pmsNotes);

  useEffect(() => {
    if (!guestId || !hotelIds?.length) return;

    DISPATCH(getGuestDetails({ guestId, hotelIds }));
    DISPATCH(getGuestStays({ guestId, hotelIds }));
    DISPATCH(getGuestReservationNotes({ guestId, hotelIds }));
  }, [guestId, hotelIds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = search.trim();

      if (trimmed.length >= 3 || trimmed.length === 0) {
        setDebouncedSearch(trimmed.toLowerCase());
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setProfileNoteValue(guest?.guestProfileNote || "");
  }, [guest?.guestProfileNote]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />

        <VuiBox
          sx={{
            minHeight: "calc(100vh - 140px)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <VuiBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <CircularProgress
              size={44}
              thickness={4}
              sx={{
                color: primary.main,
                mb: 2,
              }}
            />

            <VuiTypography
              variant="button"
              sx={{
                textAlign: "center",
                fontWeight: "normal",
                display: "block",
              }}
            >
              Loading guest profile...
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox mb={1} display="flex" alignItems="center" justifyContent="space-between">
        <VuiBox display="flex" alignItems="center" gap={0}>
          <VuiButton
            size="small"
            variant="text"
            color="primary"
            onClick={() => NAVIGATE("/guestLibrary")}
            startIcon={<FaArrowLeft size={12} />}
            sx={{
              minWidth: "unset",
              px: 2,
              py: 1,
              border: "none",
              boxShadow: "none",
              background: "transparent",
              "&:hover": {
                background: rgba(black.main, 0.04),
                border: "none",
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
            }}
          >
            Back
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {/* ===== GUEST CARD ===== */}
      <Card>
        <VuiBox py={0} px={2}>
          <VuiBox display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: 20,
                color: primary.main,
                background: rgba(primary.main, 0.1),
                border: `${borderWidth[1]} solid ${rgba(primary.main, 0.18)}`,
              }}
            >
              {initials}
            </Avatar>

            <VuiBox minWidth={0}>
              <VuiTypography variant="lg" fontWeight="bold">
                {guest.name}
              </VuiTypography>
              <GuestBadges badges={guestDetails?.badges} />
            </VuiBox>
          </VuiBox>

          {/* ===== DETAILS GRID ===== */}
          <VuiBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
            gap={2}
          >
            <VuiBox>
              <VuiTypography variant="caption">Email</VuiTypography>
              <VuiTypography variant="body2">{guest.email || "—"}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Phone</VuiTypography>
              <VuiTypography variant="body2">{guest.phone || "—"}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Nationality</VuiTypography>
              <VuiTypography variant="body2">{guest.nationality || "—"}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Language</VuiTypography>
              <VuiTypography variant="body2">{guest.language || "—"}</VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiBox
            mt={2}
            pt={1.4}
            sx={{
              borderTop: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
            }}
          >
            <VuiBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={1}
            >
              <VuiTypography variant="caption" sx={{ letterSpacing: "0.04em" }}>
                Guest Profile Note
              </VuiTypography>

              {guest.guestProfileNote && can("editGuests") && (
                <VuiButton
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={guest.guestProfileNote ? handleEditProfileNote : handleAddProfileNote}
                  sx={{
                    minWidth: "unset",
                    px: 0,
                    py: 0,
                    fontSize: "12px",
                    fontWeight: "regular",
                    textTransform: "none",
                  }}
                >
                  Edit Note
                </VuiButton>
              )}
            </VuiBox>

            {isEditingProfileNote ? (
              <VuiBox mb={2} p={1} borderRadius="12px" sx={subtleSectionStyles}>
                <VuiInput
                  gradient
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="Write a guest profile note..."
                  value={profileNoteValue}
                  onChange={(e) => setProfileNoteValue(e.target.value)}
                />

                <VuiBox display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
                  <VuiButton
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={handleCancelProfileNote}
                  >
                    Cancel
                  </VuiButton>

                  <VuiButton
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleSaveProfileNote}
                    disabled={!profileNoteValue.trim() || saveProfileNoteLoading}
                    sx={{
                      minWidth: 90,
                    }}
                  >
                    {saveProfileNoteLoading && (
                      <VuiBox display="flex" alignItems="center" gap={1}>
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{
                            color: light.main,
                            mr: 1,
                          }}
                        />
                      </VuiBox>
                    )}
                    {saveProfileNoteLoading ? "Saving..." : "Save"}
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            ) : guest.guestProfileNote ? (
              <VuiBox
                py={1}
                px={2}
                borderRadius="12px"
                sx={{
                  ...subtleSectionStyles,
                  mb: 2,
                }}
              >
                <VuiTypography
                  variant="body2"
                  lineHeight={1.6}
                  sx={{
                    fontSize: "13px",
                    letterSpacing: "0.03em",
                    opacity: 0.9,
                  }}
                >
                  {guest.guestProfileNote}
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                mb={2}
                px={1.5}
                py={1}
                borderRadius="12px"
                sx={{
                  ...dashedSectionStyles,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <VuiTypography
                  variant="caption"
                  sx={{
                    fontSize: "12px",
                    letterSpacing: "0.03em",
                  }}
                >
                  No guest profile note has been added yet.
                </VuiTypography>

                {can("editGuests") && (
                  <VuiButton
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={handleAddProfileNote}
                    sx={{
                      minWidth: "unset",
                      px: 1.5,
                      py: 0.4,
                      fontSize: "11px",
                      textTransform: "none",
                    }}
                  >
                    Add Note
                  </VuiButton>
                )}
              </VuiBox>
            )}

            <VuiTypography
              variant="caption"
              color="dark"
              fontWeight="bold"
              sx={{ letterSpacing: "0.04em", gap: 1, display: "flex", alignItems: "center" }}
            >
              <RiSparkling2Line size={12} /> Profile Summary (AI)
            </VuiTypography>

            <VuiTypography
              variant="body2"
              mt={1}
              fontWeight="light"
              lineHeight={1.6}
              sx={{
                fontSize: "13px",
                letterSpacing: "0.04em",
                opacity: 0.9,
              }}
            >
              {guest.aiSummary || "No AI profile summary available for this guest."}
            </VuiTypography>
          </VuiBox>
          {/* <VuiBox mt={2} pt={2} borderTop="1px solid rgba(255,255,255,0.12)">
            <VuiTypography
              variant="caption"
              
              sx={{ letterSpacing: "0.04em", gap: 1, display: "flex", alignItems: "center" }}
            >
              <RiSparkling2Line size={12} /> Profile Summary (AI)
            </VuiTypography>

            <VuiTypography
              variant="body2"
              
              mt={1}
              fontWeight="light"
              lineHeight={1.6}
              sx={{
                fontSize: "14px",
                letterSpacing: "0.04em",
                opacity: 0.9,
              }}
            >
              {
                "This guest is a frequent traveler who prefers quiet rooms and minimal interaction. Often requests late check-ins and values a smooth, efficient arrival experience."
              }
            </VuiTypography>
          </VuiBox> */}
        </VuiBox>
      </Card>

      {/* ===== STATS ===== */}
      <VuiBox
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(180px,1fr))"
        gap={2}
        mt={2}
      >
        <MiniStatisticsCard
          title={{ text: "Total Stays", fontWeight: "regular" }}
          count={guest.stays || 0}
          icon={{ color: "primary", component: <FaBed size="18px" /> }}
          direction="right"
        />

        <MiniStatisticsCard
          title={{ text: "First Stay", fontWeight: "regular" }}
          count={formatDate(guest.firstStay)}
          icon={{ color: "primary", component: <FaHistory size="18px" /> }}
        />

        <MiniStatisticsCard
          title={{ text: "Recent Stay", fontWeight: "regular" }}
          count={formatDate(guest.lastStay)}
          icon={{ color: "primary", component: <FaCalendarCheck size="18px" /> }}
        />

        <MiniStatisticsCard
          title={{ text: "Avg Stay Duration", fontWeight: "regular" }}
          count={`${guest.avgStayDuration || 0} days`}
          icon={{ color: "primary", component: <FaClock size="18px" /> }}
        />
      </VuiBox>

      {/* ===== TABS ===== */}
      <Card sx={{ mt: 2 }}>
        <AppBar position="static" color="transparent" elevation={0}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              background: "transparent",
              display: "flex",
              justifyContent: "flex-end",

              "& .MuiTab-root": {
                minWidth: { xs: "auto", md: 350 },
                px: { xs: 2, sm: 3 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                whiteSpace: "nowrap",
              },
            }}
          >
            <Tab value={0} label="Stay History" icon={<FaBed size={14} />} iconPosition="start" />

            <Tab
              value={1}
              label="Notes History"
              icon={<FaStickyNote size={14} />}
              iconPosition="start"
            />
          </Tabs>
        </AppBar>

        <VuiBox p={2}>
          {/* STAYS */}
          {tab === 0 && (
            <VuiBox>
              {/* ===== FILTERS ===== */}
              <VuiBox display="flex" gap={1.5} mb={2} alignItems="center" justifyContent="end">
                <VuiBox sx={{ width: { xs: "150px", md: "240px" } }}>
                  <VuiInput
                    gradient
                    fullWidth
                    placeholder="Search by room or hotel..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </VuiBox>

                <DateRangeFilter value={dateRange} onChange={setDateRange} />
              </VuiBox>

              {/* ===== DATA ===== */}
              {filteredStays.map((stay, index) => (
                <StayTile key={index} stay={stay} />
              ))}
            </VuiBox>
          )}

          {/* NOTES */}
          {tab === 1 && (
            <VuiBox>
              <VuiBox display="flex" justifyContent="end" alignItems="center" mb={2} gap={1.5}>
                <VuiBox sx={{ width: { xs: "150px", md: "240px" } }}>
                  <VuiInput
                    gradient
                    fullWidth
                    placeholder="Search notes..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </VuiBox>
              </VuiBox>

              <VuiBox
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))"
                gap={1.5}
              >
                {arrivalNotes.length > 0 && (
                  <NotesColumn
                    title="Arrival Notes"
                    notes={arrivalNotesFiltered}
                    type="arrival"
                    emptyMessage="No matching arrival notes found."
                  />
                )}

                {staffNotes.length > 0 && (
                  <NotesColumn
                    title="Staff Notes"
                    notes={staffNotesFiltered}
                    type="staff"
                    emptyMessage="No matching staff notes found."
                  />
                )}

                {pmsNotes.length > 0 && (
                  <NotesColumn
                    title="PMS Notes"
                    notes={pmsNotesFiltered}
                    type="pms"
                    emptyMessage="No matching PMS notes found."
                  />
                )}
              </VuiBox>
            </VuiBox>
          )}
        </VuiBox>
      </Card>
    </DashboardLayout>
  );
};

export default ProfileHistory;
